import { useCallback, useMemo } from 'react'
import { AvailableSettings, BulkEmailExemptPolicy, DomainEntry } from 'types/Settings'
import { useRefField } from 'lib/settingsForm/useRefField'
import { SettingsForm, useSettingsForm } from 'lib/settingsForm/useSettingsForm'
import { useFormatMessage } from 'lib/localization'
import { isEmailOrDomainValid } from 'lib/validation'

export interface BulkEmailExemptTableFormState {
  name: string
  comment: string
  type: AvailableSettings.BULK_EMAIL_SENDER_EXEMPT | AvailableSettings.BULK_EMAIL_RCPT_EXEMPT
}

export interface ValidatorDependencies {
  data: BulkEmailExemptPolicy[]
}

export interface BulkEmailExemptTableFormConfig {
  initialState: BulkEmailExemptTableFormState
  delegateIsDirtyForm?: (isDirty: boolean) => void
}

export type BulkEmailExemptTableForm = SettingsForm<BulkEmailExemptTableFormState>

const BASE_I18N_KEY = 'ess.inbound_settings.reputation.error'

export const useBulkEmailExemptTableForm = ({
  initialState,
  delegateIsDirtyForm
}: BulkEmailExemptTableFormConfig): BulkEmailExemptTableForm => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const nameField = useRefField(initialState.name)
  const commentField = useRefField(initialState.comment)
  const typeField = useRefField(initialState.type)

  const formConfig = useMemo(
    () => ({
      fields: {
        name: nameField,
        comment: commentField,
        type: typeField
      },
      delegateIsDirtyForm
    }),
    [commentField, delegateIsDirtyForm, nameField, typeField]
  )
  const form = useSettingsForm<BulkEmailExemptTableFormState>(formConfig)

  const validate = useCallback(
    (newEntry: BulkEmailExemptPolicy, dependencies: ValidatorDependencies) => {
      form.setError('')

      if (newEntry.name.startsWith('*@')) {
        form.setError(formatMessage('wildcard_domain'))
        return false
      }

      const findDuplicate = dependencies.data.find(
        entry => entry.name === newEntry.name && entry.type === newEntry.type
      )
      if (findDuplicate) {
        form.setError(formatMessage('duplicate_value', { item: findDuplicate.name }))
        return false
      }

      if (!newEntry.name) {
        form.setError(formatMessage('empty_domain'))
        return false
      }

      if (!isEmailOrDomainValid(newEntry.name)) {
        form.setError(formatMessage('invalid_email_domain'))
        return false
      }

      return true
    },
    [form, formatMessage]
  )

  return useMemo(
    () => ({
      ...form,
      validate
    }),
    [form, validate]
  )
}
